/* eslint-disable camelcase */
import React, { useState, useEffect, useRef, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { gql, useQuery, useMutation, useApolloClient } from '@apollo/client';
import useStyles from 'isomorphic-style-loader/useStyles';
import get from 'lodash/get';
import TimeAgo from 'javascript-time-ago';
import en from 'javascript-time-ago/locale/en.json';

// MaterialUI
import Button from '@material-ui/core/Button';
import Tabs from '@material-ui/core/Tabs';
import Tab from '@material-ui/core/Tab';

// app modules: services
import history from 'services/history';

import { USER_UPDATE_INFO } from 'constants/index';
import { UPDATE_USER_DISPLAY_NAME } from 'queries/userQueries';

// app: components
import CommentEditActionsMenu from 'components/Comments/CommentEditActionsMenu';
import CommentStream from 'components/Comments/CommentStream';
import NoteComment from 'components/Comments/Comment/NoteComment';
import NoteForm from 'components/Comments/CommentForm/NoteForm';
import QuestionForm from 'components/Comments/CommentForm/QuestionForm';
import ReviewForm from 'components/Comments/CommentForm/ReviewForm';

// app: hooks
import useDisableSwiping from 'hooks/useDisableSwiping';
import useLocalstorageState from 'hooks/useLocalstorageState';
import useHandleImageUpload from 'hooks/useHandleImageUpload';

// assets
import CssComments from './Comments.scss';
import { GET_USER_COMMENTS, SAVE_COMMENTS } from './CommentQueries';

export const CommentsContext = React.createContext({});

TimeAgo.setDefaultLocale(en.locale);
TimeAgo.addLocale(en);

const Comments = props => {
  const { id, hash, asAuthor, readyToScroll, onLoad, setUserRecipeReview, hasPrimaryImage } = props;
  useStyles(CssComments);

  const apolloClient = useApolloClient();
  const {
    display_name: displayName,
    logged_in: loggedIn,
    auth0_id: auth0Id,
    id: userId,
  } = useSelector(state => state.user);
  const clockOffset = useSelector(state => state.user.clock_offset);
  const displayType = useSelector(state => state.dimensions.displayType);
  const urlHash = useSelector(state => (readyToScroll ? state.router.hash : ''));
  const isModerator = useSelector(
    state => state.user.permissions && state.user.permissions.includes('comment:moderator'),
  );

  const [, deepLinkType, deepLinkId, deepLinkOptionCode] =
    urlHash.match(/^#(review|question|note)(\d+)([a-z]{0,1})$/i) || [];
  const [currentTab, setCurrentTab] = useState('review');
  const [previousTab, setPreviousTab] = useState(null);
  const [formOpen, setFormOpen] = useState(false);
  const [selectedComment, setSelectedComment] = useState(null);
  const [draft, setDraft, clearDraft, localStorageError] = useLocalstorageState(
    `draft-comment-v1-${id}`,
    {},
  );
  const [focused, setFocused] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const textFieldRef = useRef(null);

  const [justUpdatedTab, setJustUpdatedTab] = useState();
  const [hasOwnQuestions, setHasOwnQuestions] = useState(false);

  const {
    setReviewImage,
    callDeleteImage,
    callUploadImage,
    handleRemoveExistingImage,
    setUploadingError,
    isHandlingImageUpload,
    removedExistingImage,
    uploadingError,
    reviewImage,
  } = useHandleImageUpload();

  const handleOnLoad = data => {
    const { RecipeComments } = data;
    const myComments = RecipeComments.filter(
      comment => comment.user.id === userId && comment.type === 'question',
    );

    setSelectedComment(null);
    setHasOwnQuestions(myComments.length > 0);

    onLoad(data);
  };

  /**
   * ========================================
   * Beginning of display_name update section
   * ========================================
   * */
  const [isEditingName, setIsEditingName] = useState(false);
  const [newName, setNewName] = useState('');
  const dispatch = useDispatch();

  const handleNameEditEnd = () => {
    setNewName('');
    setIsEditingName(false);
  };

  const [updateDisplayName, { loading: updateDisplayNameProgress }] = useMutation(
    UPDATE_USER_DISPLAY_NAME,
    {
      variables: {
        input: {
          displayName: newName,
          auth0Id,
        },
      },
      fetchPolicy: 'no-cache',
      async onCompleted() {
        await dispatch({
          type: USER_UPDATE_INFO,
          payload: {
            display_name: newName,
          },
        });

        handleNameEditEnd();
        toast.success(`Successfully updated display name to "${newName}"`);
      },
      onError: err => {
        console.error('Failed to update display name', {
          graphQLErrors: err.graphQLErrors,
          networkError: err.networkError,
        });
      },
    },
  );

  const handleOnNewNameSubmit = name => {
    setNewName(name);

    if (name === displayName) {
      handleNameEditEnd();
      return;
    }

    setTimeout(() => {
      updateDisplayName().then();
    });
  };

  /**
   * ========================================
   * Ending of display_name update section
   * ========================================
   * */

  // Swipe on text fields shouldn't trigger swipeToBrowse
  useDisableSwiping(textFieldRef);

  // Reset when the recipe changes
  useEffect(
    () => {
      setFormOpen(false);
      setFocused(false);
      setSaveError(null);

      if (deepLinkType) {
        setCurrentTab(deepLinkType);
      }

      return () => {
        // Prune the cache when the component unmounts or changes recipes
        apolloClient.cache.evict({
          id: 'ROOT_QUERY',
          fieldName: 'RecipeComments',
          broadcast: false,
        });
      };
    },
    [id, deepLinkType, apolloClient.cache],
  );

  // URL hashes #question, #note should open the form and scroll to it
  useEffect(
    () => {
      const target = urlHash.substring(1);
      if (target === 'question' || target === 'note' || target === 'review') {
        history.replace({ hash: '' });
        setCurrentTab(target);
        setFormOpen(true);

        setTimeout(() => {
          if (textFieldRef.current) {
            textFieldRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
            if (target === 'review') {
              setTimeout(() => {
                setFocused(true);
              }, 500);
            } else {
              setFocused(true);
              textFieldRef.current.focus();
            }
          }
        }, 1000);
      }
    },
    [urlHash],
  );

  // Show the notes tab for printing
  useEffect(() => {
    const handlePrintStart = () => {
      setPreviousTab(currentTab);
      setCurrentTab('note');
    };
    const handlePrintEnd = () => {
      setCurrentTab(previousTab);
      setPreviousTab(null);
    };
    window.addEventListener('beforeprint', handlePrintStart);
    window.addEventListener('afterprint', handlePrintEnd);
    return () => {
      window.removeEventListener('beforeprint', handlePrintStart);
      window.removeEventListener('afterprint', handlePrintEnd);
    };
  });

  // Clear drafts if there is an error writing to localstorage
  useEffect(
    () => {
      if (localStorageError && window.localStorage) {
        console.warn('Local storage is full. Clearing draft comments', localStorageError);
        Object.keys(window.localStorage)
          .filter(key => key.startsWith('draft-comment-v1-') && key !== `draft-comment-v1-${id}`)
          .forEach(key => {
            window.localStorage.removeItem(key);
          });
      }
    },
    [localStorageError, id],
  );

  const handleFormClose = () => {
    setFormOpen(false);
    clearDraft();
    setSaveError(null);
  };

  // Load this user's comments
  const { error: userFetchError, data: userCommentData } = useQuery(GET_USER_COMMENTS, {
    variables: {
      content_id: id,
      _authenticated: true,
    },
    ssr: false,
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
    onError: err => {
      console.error('Failed to load user comments', err);
    },
    onCompleted: data => {
      const { UserComments, RecipeCommentCounts } = data || {
        UserComments: [],
        RecipeCommentCounts: {},
      };

      if (
        UserComments &&
        UserComments.find(userComment => userComment.type === 'review') &&
        setUserRecipeReview
      ) {
        setUserRecipeReview(UserComments.find(userComment => userComment.type === 'review'));
      }

      if (justUpdatedTab && justUpdatedTab !== currentTab) {
        setCurrentTab(justUpdatedTab);
        setJustUpdatedTab(undefined);
        return;
      }

      if (currentTab && justUpdatedTab === currentTab && deepLinkType) {
        return;
      }

      const hasQuestion = Boolean(RecipeCommentCounts.question);
      const hasReview = Boolean(RecipeCommentCounts.review);

      if (hasQuestion && hasReview) {
        setCurrentTab('review');
      } else if (hasReview) {
        setCurrentTab('review');
      } else if (hasQuestion) {
        setCurrentTab('question');
      } else if (
        UserComments &&
        Boolean(UserComments.find(userComment => userComment.type === 'note'))
      ) {
        setCurrentTab('note');
      } else {
        setCurrentTab('review');
      }
    },
  });

  const [saveComments, { loading: saveInProgress }] = useMutation(SAVE_COMMENTS, {
    onCompleted() {
      setDraft({});
      setSelectedComment(null);
      handleFormClose();
    },
    onError: err => {
      console.error('Failed to save comment', {
        graphQLErrors: err.graphQLErrors[0].extensions.code,
        networkError: err.networkError,
      });
      const errorCode = get(err, 'graphQLErrors[0].extensions.code');
      const niceErrorCode = errorCode ? ` (code: ${errorCode})` : '';
      const niceSaveError =
        get(err, 'graphQLErrors[0].message') ||
        `We can't save this right now. Please try again later${niceErrorCode}`;
      setSaveError(niceSaveError);
    },
    refetchQueries: ['GetAllRecipeComments'],
    update(cache, { data: updatedData }) {
      const {
        UserComment: updatedComment,
        UserRecipeFeedback: updatedFeedback,
        RecipeCommentCounts: updatedCommentCounts,
      } = updatedData;
      // If the query results don't include this Comment then update the query
      if (
        !userCommentData.UserComments.find(
          existingComment => existingComment.id === updatedComment.id,
        )
      ) {
        cache.writeQuery({
          query: GET_USER_COMMENTS,
          variables: {
            content_id: id,
          },
          data: {
            UserRecipeFeedback: updatedFeedback,
            UserComments: [...userCommentData.UserComments, updatedComment],
            RecipeCommentCounts: updatedCommentCounts,
          },
        });
      }
      // Update the cached average rating of the recipe
      if (updatedFeedback.ratings) {
        cache.writeFragment({
          data: { ratings: updatedFeedback.ratings },
          fragment: gql`
            fragment RecipeRating on Recipe {
              ratings {
                average
                count
              }
            }
          `,
          id: `Recipe:${id}:full`,
        });
        cache.writeFragment({
          data: { ratings: updatedFeedback.ratings },
          fragment: gql`
            fragment RecipeRating on Recipe {
              ratings {
                average
                count
              }
            }
          `,
          id: `Recipe:${id}:preview`,
        });
      }
    },
  });
  const UserRecipeFeedback = get(userCommentData, 'UserRecipeFeedback') || {};
  const UserComments = get(userCommentData, 'UserComments') || [];
  const comment = UserComments.find(_comment => _comment.type === currentTab);
  const rawComment = get(comment, 'raw_text') || '';

  // Only use the draft if it is newer than the saved comment, or if there is no saved comment
  const draftIsValid =
    typeof draft?.updated_at === 'number' &&
    (!comment || new Date(comment.updated_at).getTime() < draft.updated_at);

  const handleTabChange = (event, value) => {
    setFormOpen(false);
    setFocused(false);
    setCurrentTab(value);
  };

  const handleFormSubmit = async values => {
    if (removedExistingImage && !uploadingError) {
      await callDeleteImage(id);
    }

    if (reviewImage) {
      await callUploadImage(id);
    }

    const variables = {
      content_id: id,
      content_hash: hash,
      type: currentTab,
      raw_text: values[currentTab],
      draft: false, // TODO
      is_new: values.is_new,
    };

    if (values.is_new === false && values.id) {
      variables.id = values.id;
    }

    if (values.rating) {
      variables.star_rating = values.rating;
    }

    if (typeof values.didCook === 'boolean') {
      variables.did_cook = values.didCook;
    }

    if (selectedComment && selectedComment?.id) {
      variables.id = selectedComment?.id;
      variables.is_new = false;
    }

    setJustUpdatedTab(currentTab);

    await saveComments({
      variables,
    });
  };

  const handleFormOpen = () => {
    if (loggedIn) {
      setFormOpen(true);
      setSaveError(null);
    } else {
      let signupMessage;
      switch (currentTab) {
        case 'review':
          signupMessage = 'Join ckbk free to review this recipe';
          break;
        case 'question':
          signupMessage = 'Join ckbk free to ask and answer questions';
          break;
        case 'note':
          signupMessage = 'Join ckbk free to add your notes to this recipe';
          break;
        default:
          signupMessage = null;
          break;
      }
      history.push('/subscribe', { returnTo: history.location, signupMessage });
    }
  };

  const handleEditQuestionClick = value => {
    setSelectedComment(value);

    const timestamp = new Date().getTime() - clockOffset;
    setDraft({
      ...value,
      question: get(value, 'safe_html'),
      updated_at: timestamp,
    });
    handleFormOpen();
  };

  const handleFormChange = values => {
    const timestamp = new Date().getTime() - clockOffset;
    setDraft(
      draft ? { ...draft, ...values, updated_at: timestamp } : { ...values, updated_at: timestamp },
    );
    if (!(values?.review || values?.question || values?.note)) {
      setTimeout(clearDraft, 0);
    }
  };

  const [questionsCount, reviewsCount] = useMemo(
    () => {
      const qsCount = get(userCommentData, `RecipeCommentCounts.question`);
      const rvsCount = get(userCommentData, `RecipeCommentCounts.review`);

      const qsCountString = qsCount ? ` (${qsCount})` : '';
      const rvsCountString = rvsCount ? ` (${rvsCount})` : '';
      return [qsCountString, rvsCountString];
    },
    [userCommentData],
  );

  let contentBelowNote = null;
  if (currentTab === 'note') {
    if (comment && comment.safe_html && !formOpen) {
      contentBelowNote = <NoteComment comment={comment} onEdit={handleFormOpen} />;
    } else if (!loggedIn) {
      contentBelowNote = (
        <div className="comments__empty margin-top-10">
          <p className="margin-bottom-10">
            ckbk members can save private notes alongside each recipe
          </p>
          <Button onClick={handleFormOpen} variant="contained" color="primary">
            Join ckbk
          </Button>
        </div>
      );
    } else {
      contentBelowNote = (
        <div className="comments__empty padding-top-10 padding-bottom-40">
          <p>Notes entered here are only visible to you</p>
        </div>
      );
    }
  }

  const editOptionsMenu = comment ? (
    <CommentEditActionsMenu
      contentId={id}
      commentId={comment.id}
      type={comment.type}
      onDeleteError={setSaveError}
      onDeleteCompleted={handleFormClose}
    />
  ) : null;

  return (
    <CommentsContext.Provider value={{ asAuthor }}>
      <div className="comments">
        <div className="comments__tabs">
          <Tabs
            variant="fullWidth"
            textColor="primary"
            value={currentTab}
            onChange={handleTabChange}
            aria-label="tabs"
            indicatorColor="primary"
            selectionFollowsFocus
          >
            <Tab label={`Reviews${reviewsCount}`} value="review" />
            <Tab
              label={
                displayType === 'mobile'
                  ? `Q & A${questionsCount}`
                  : `Questions & Answers${questionsCount}`
              }
              value="question"
            />
            <Tab label="My Notes" value="note" />
          </Tabs>
        </div>
        {userFetchError ? <p>Something went wrong loading your {currentTab}s</p> : null}
        {currentTab === 'review' ? (
          <>
            <ReviewForm
              didCook={
                typeof draft?.didCook === 'boolean' && draftIsValid
                  ? draft.didCook
                  : UserRecipeFeedback.did_cook
              }
              focused={focused}
              isOpen={formOpen}
              isModerator={isModerator}
              onBlur={() => setFocused(false)}
              onChange={handleFormChange}
              onChangeTab={handleTabChange}
              onClose={handleFormClose}
              onFocus={() => setFocused(true)}
              onOpen={handleFormOpen}
              onSubmit={handleFormSubmit}
              disabledNameField={updateDisplayNameProgress}
              onNameSubmit={handleOnNewNameSubmit}
              optionsMenu={editOptionsMenu}
              rating={draft?.rating && draftIsValid ? draft.rating : UserRecipeFeedback.star_rating}
              ref={textFieldRef}
              review={
                typeof draft?.review === 'string' && draftIsValid ? draft.review : rawComment || ''
              }
              comment={comment}
              saveError={saveError}
              saveInProgress={saveInProgress}
              showAsEditButton={!!rawComment}
              showAuthorBadge={asAuthor}
              isEditingName={isEditingName}
              setIsEditingName={setIsEditingName}
              uploadingError={uploadingError}
              setUploadingError={setUploadingError}
              setReviewImage={setReviewImage}
              isUploadingImage={isHandlingImageUpload}
              onRemoveExistingImage={handleRemoveExistingImage}
            />
          </>
        ) : null}
        {currentTab === 'question' ? (
          <QuestionForm
            focused={focused}
            question={
              draft && typeof draft?.question === 'string' && draftIsValid ? draft.question : ''
            }
            commentId={selectedComment && selectedComment?.id}
            isOpen={formOpen}
            onChange={handleFormChange}
            onClose={handleFormClose}
            onFocus={() => setFocused(true)}
            onBlur={() => setFocused(false)}
            onOpen={handleFormOpen}
            onSubmit={handleFormSubmit}
            optionsMenu={editOptionsMenu}
            hasOwnQuestions={hasOwnQuestions}
            ref={textFieldRef}
            saveError={saveError}
            saveInProgress={saveInProgress}
          />
        ) : null}
        {currentTab === 'note' ? (
          <React.Fragment>
            <NoteForm
              focused={focused}
              isModerator={isModerator}
              isOpen={formOpen}
              note={typeof draft?.note === 'string' && draftIsValid ? draft.note : rawComment || ''}
              comment={comment}
              onBlur={() => setFocused(false)}
              onChange={handleFormChange}
              onClose={handleFormClose}
              onFocus={() => setFocused(true)}
              onOpen={handleFormOpen}
              onSubmit={handleFormSubmit}
              ref={textFieldRef}
              saveError={saveError}
              saveInProgress={saveInProgress}
              showAsEditButton={!!rawComment}
            />
            {contentBelowNote}
          </React.Fragment>
        ) : null}
        {currentTab === 'note' ? null : (
          <CommentStream
            asAuthor={asAuthor}
            contentId={id}
            hasPrimaryImage={hasPrimaryImage}
            type={currentTab}
            isModerator={isModerator}
            onEditQuestionClick={handleEditQuestionClick}
            focusedCommentId={parseInt(deepLinkId, 10)}
            focusedCommentResponding={deepLinkOptionCode === 'r'}
            fallbackComment={comment}
            onLoad={handleOnLoad}
            readyToScroll={readyToScroll}
            showPlaceholders={!formOpen}
          />
        )}
      </div>
    </CommentsContext.Provider>
  );
};

Comments.propTypes = {
  asAuthor: PropTypes.bool,
  hash: PropTypes.string,
  hasPrimaryImage: PropTypes.bool,
  id: PropTypes.string.isRequired,
  onLoad: PropTypes.func,
  setUserRecipeReview: PropTypes.func,
  readyToScroll: PropTypes.bool,
};

Comments.defaultProps = {
  asAuthor: false,
  hasPrimaryImage: false,
  hash: null,
  onLoad: () => {},
  setUserRecipeReview: () => {},
  readyToScroll: false,
};

export default React.memo(Comments);
